import { useCallback, useEffect, useState } from 'react';
import {
  addProjectMember,
  fetchProjectMembers,
  fetchProjects,
  removeProjectMember,
} from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import type { Id, Project } from '../../api/types';
import {
  invertMemberships,
  lastLeadWarning,
  type ProjectMember,
  type UserProject,
} from './membership';

/**
 * EVERY PROJECT'S MEMBERSHIP, LOADED ONCE AND INVERTED — PH-56.
 *
 * ⚠️ N+1 REQUESTS, STATED PLAINLY RATHER THAN HIDDEN. There is no `GET /users/:id/projects`, so
 * answering "which projects is this person in" means reading every project's member list. That is 1
 * request for the project list plus N for their members — 10 today. It is fine at this size and it
 * will not be at 200; when that day comes the fix is an endpoint, not a cleverer client.
 *
 * ⭐ LOADED ONCE FOR THE WHOLE PANEL, not per user card. Ten users each fetching nine projects would
 * be ninety requests for the same nine answers.
 *
 * ⛔ A FAILED PROJECT IS OMITTED FROM THE MAP, NOT RECORDED AS EMPTY. `invertMemberships` skips a
 * project id it has no entry for, so "we could not ask" never renders as "this project has no
 * members" — a distinction that would otherwise make an admin think they had removed someone.
 */
export interface MembershipsState {
  /** Every project, for the "add to project" picker. */
  projects: Project[];
  /** user id → the projects they are in. ⚠️ A user absent from the map is in NO project. */
  byUser: Map<string, UserProject[]>;
  /** project id → its members, for the last-lead check. */
  byProject: Map<string, ProjectMember[]>;
  /**
   * 🔴 `hasLoaded`, NOT `isLoading`, AND THE DIFFERENCE IS A REAL DEFECT THIS REPLACED.
   *
   * `isLoading` starts FALSE. So between the user list arriving (which renders the section) and the
   * effect actually starting, there is a window where isLoading is false and the data is absent —
   * and absent data here MEANS "in no project". Every user was briefly accused of it.
   * ⭐ "Have we finished asking?" has no such window: it is false from the first render until an
   * answer exists, which is exactly the question the screen needs to ask.
   */
  hasLoaded: boolean;
  /** Projects whose member list could not be read, by key — shown, never silently dropped. */
  unreadable: string[];
  error: string | null;
  busyUserId: string | null;
  reload: () => void;
  /**
   * ⭐ BOTH RESOLVE TO WHETHER THE WRITE ACTUALLY LANDED, not merely to "the attempt finished".
   * A dialog that closes on the promise settling would dismiss itself on a REFUSAL — closing the
   * one surface able to show the error. `useCompleteSprint` states that rule; this is how a caller
   * can honour it.
   */
  add: (userId: Id, projectId: Id, role: 'member' | 'lead') => Promise<boolean>;
  remove: (userId: Id, projectId: Id) => Promise<boolean>;
  /** The sentence to show before removing, or null. See `lastLeadWarning`. */
  warnBeforeRemove: (userId: Id, projectId: Id) => string | null;
}

export function useMemberships(enabled: boolean): MembershipsState {
  const [projects, setProjects] = useState<Project[]>([]);
  const [byProject, setByProject] = useState<Map<string, ProjectMember[]>>(new Map());
  const [unreadable, setUnreadable] = useState<string[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const controller = new AbortController();

    void (async () => {
      setError(null);
      try {
        const all = await fetchProjects(controller.signal);
        if (cancelled) return;
        setProjects(all);

        /**
         * ⭐ `allSettled`, NOT `all`. One project a reader cannot see must not blank the whole
         *   screen — and it is a real case, because the member list is readable by any signed-in
         *   user but a project could still 404 if it was archived between the two calls.
         */
        const settled = await Promise.allSettled(
          all.map((project) => fetchProjectMembers(project.id, controller.signal)),
        );
        if (cancelled) return;

        const map = new Map<string, ProjectMember[]>();
        const failed: string[] = [];
        settled.forEach((outcome, index) => {
          const project = all[index];
          if (!project) return;
          if (outcome.status === 'fulfilled') map.set(String(project.id), outcome.value);
          else failed.push(project.key);
        });
        setByProject(map);
        setUnreadable(failed);
      } catch (caught) {
        if (cancelled) return;
        const apiError = toApiError(caught, '/projects');
        setError(apiError.reason ?? apiError.message);
      } finally {
        // ⭐ Set on FAILURE too. A load that errored has finished asking, and leaving hasLoaded
        //   false would render "Loading projects…" forever with the error banner beside it.
        if (!cancelled) setHasLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  /**
   * ⛔ BOTH WRITES RELOAD FROM THE SERVER rather than patching local state. A membership change can
   * be refused, and it can also succeed differently from what was asked (the API defaults a role).
   * Re-reading is the only version that cannot drift from what is actually stored — the same reason
   * every write in this app returns the resulting row.
   */
  const runWrite = useCallback(
    async (userId: Id, path: string, action: () => Promise<void>): Promise<boolean> => {
      setBusyUserId(String(userId));
      setError(null);
      try {
        await action();
        reload();
        return true;
      } catch (caught) {
        const apiError = toApiError(caught, path);
        setError(apiError.reason ?? apiError.message);
        return false;
      } finally {
        setBusyUserId(null);
      }
    },
    [reload],
  );

  const add = useCallback(
    (userId: Id, projectId: Id, role: 'member' | 'lead') =>
      runWrite(userId, `/projects/${projectId}/members`, () =>
        addProjectMember(projectId, userId, role),
      ),
    [runWrite],
  );

  const remove = useCallback(
    (userId: Id, projectId: Id) =>
      runWrite(userId, `/projects/${projectId}/members/${userId}`, () =>
        removeProjectMember(projectId, userId),
      ),
    [runWrite],
  );

  const warnBeforeRemove = useCallback(
    (userId: Id, projectId: Id) =>
      lastLeadWarning(byProject.get(String(projectId)) ?? [], userId),
    [byProject],
  );

  return {
    projects,
    byUser: invertMemberships(projects, byProject),
    byProject,
    hasLoaded,
    unreadable,
    error,
    busyUserId,
    reload,
    add,
    remove,
    warnBeforeRemove,
  };
}
